'use client';

import { useState, useEffect } from 'react';
import type { ActiveAuctionsResponse } from '../../services/api';
import { calculateTimeRemaining } from './utils';
import { AUCTION_TIMER_INTERVAL } from './constants';

type Auction = ActiveAuctionsResponse['auctions'][0];

interface UseAuctionTimerOptions {
  auctions: Auction[];
  onExpired?: () => void;
}

interface UseAuctionTimerResult {
  localTimeRemaining: Record<string, number>;
}

export const useAuctionTimer = ({
  auctions,
  onExpired,
}: UseAuctionTimerOptions): UseAuctionTimerResult => {
  const [localTimeRemaining, setLocalTimeRemaining] = useState<Record<string, number>>({});

  // Create stable key from auctions array (ids + endTimes)
  const auctionsKey = auctions.map(a => `${a.id}:${a.endTime}`).join(',');

  useEffect(() => {
    if (auctions.length === 0) return;

    const timers: Record<string, number> = {};
    auctions.forEach((auction) => {
      timers[auction.id] = calculateTimeRemaining(auction);
    });
    setLocalTimeRemaining(timers);

    const interval = setInterval(() => {
      setLocalTimeRemaining((prevTimers) => {
        const newTimers: Record<string, number> = {};
        let needsReload = false;

        auctions.forEach((auction) => {
          const remaining = calculateTimeRemaining(auction);
          newTimers[auction.id] = remaining;

          if (remaining === 0 && (prevTimers[auction.id] || 0) > 0) {
            needsReload = true;
          }
        });

        if (needsReload && onExpired) {
          onExpired();
        }

        return newTimers;
      });
    }, AUCTION_TIMER_INTERVAL);

    return () => clearInterval(interval);
  }, [auctionsKey, onExpired]);

  return {
    localTimeRemaining,
  };
};
